import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import {
  ClipboardList, PlusCircle, Search, Trash2, ChevronRight, CheckCircle2,
  AlertTriangle, LockKeyhole, PackageSearch,
} from 'lucide-react';

const REASONS = [
  { value: 'waste', label: 'Waste' },
  { value: 'spillage', label: 'Spillage' },
  { value: 'delivery_short', label: 'Delivery short' },
  { value: 'theft', label: 'Theft' },
  { value: 'correction', label: 'Correction' },
  { value: 'other', label: 'Other' },
];

/**
 * Kiosk stock take flow.
 * Two modes:
 *  - No active session: pick a count sheet or start a Free count.
 *  - Active draft session: list of items with counts + keypad entry, approve.
 */
export default function StockTakeTab({ kApi, theme, session, refreshToday, sessionKey }) {
  const t = (dark, light) => (theme === 'light' ? light : dark);

  const [sheets, setSheets] = useState([]);
  const [canStart, setCanStart] = useState(true);
  const [canApprove, setCanApprove] = useState(false);
  const [pushMode, setPushMode] = useState('on_approval');
  const [currentSession, setCurrentSession] = useState(null);
  const [loading, setLoading] = useState(true);

  // Line entry dialog
  const [lineDialog, setLineDialog] = useState(null); // { variation_external_id, item_name, previous_count }
  const [lineQty, setLineQty] = useState('');
  const [lineReason, setLineReason] = useState('none');
  const [lineNote, setLineNote] = useState('');

  // Free-count item search
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchProvider, setSearchProvider] = useState(null);
  const [searchError, setSearchError] = useState(null);

  // Approval sheet
  const [approveOpen, setApproveOpen] = useState(false);
  const [approving, setApproving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sheetsRes, currentRes] = await Promise.all([
        kApi().get('/stock-take/sheets'),
        kApi().get('/stock-take/current'),
      ]);
      setSheets(sheetsRes.data.sheets || []);
      setCanStart(!!sheetsRes.data.can_start);
      setCanApprove(!!sheetsRes.data.can_approve);
      setPushMode(sheetsRes.data.push_mode || 'on_approval');
      setCurrentSession(currentRes.data.session || null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load stock take');
    } finally {
      setLoading(false);
    }
  }, [kApi]);

  // sessionKey changes when the auth session changes (PIN swap), so we refetch.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load, sessionKey]);

  const startSession = async (kind, sheetId = null) => {
    try {
      const res = await kApi().post('/stock-take/start', { kind, sheet_id: sheetId });
      setCurrentSession(res.data.session);
      toast.success(kind === 'template' ? 'Count started' : 'Free count started');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to start');
    }
  };

  const openLine = async (item) => {
    setLineDialog({
      variation_external_id: item.variation_external_id,
      item_name: item.name,
      previous_count: item.previous_count ?? item.in_stock ?? null,
    });
    setLineQty('');
    setLineReason('none');
    setLineNote('');
  };

  const saveLine = async () => {
    const qty = parseFloat(lineQty);
    if (Number.isNaN(qty) || qty < 0) return toast.error('Enter a valid count');
    try {
      await kApi().post(`/stock-take/${currentSession.id}/lines`, {
        variation_external_id: lineDialog.variation_external_id,
        item_name: lineDialog.item_name,
        new_count: qty,
        reason: lineReason === 'none' ? null : lineReason,
        reason_note: lineNote || null,
      });
      const res = await kApi().get(`/stock-take/${currentSession.id}`);
      setCurrentSession(res.data.session);
      setLineDialog(null);
      toast.success('Line saved');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save line');
    }
  };

  const deleteLine = async (lineId) => {
    try {
      await kApi().delete(`/stock-take/${currentSession.id}/lines/${lineId}`);
      const res = await kApi().get(`/stock-take/${currentSession.id}`);
      setCurrentSession(res.data.session);
      toast.success('Line removed');
    } catch {
      toast.error('Failed');
    }
  };

  const approve = async () => {
    setApproving(true);
    try {
      const res = await kApi().post(`/stock-take/${currentSession.id}/approve`);
      const { push_status, push_error, pushed_line_count, failed_line_count, movement_count } = res.data;
      if (push_status === 'pushed') {
        toast.success(`Approved — ${movement_count} lines saved, POS updated`);
      } else if (push_status === 'partial') {
        toast.warning(`Approved with partial POS push: ${pushed_line_count} ok, ${failed_line_count} failed. Retry from Inventory.`);
      } else if (push_status === 'failed') {
        toast.warning(`Approved — POS push failed: ${push_error || 'unknown'}. Retry from Inventory.`);
      } else {
        toast.success(`Approved — ${movement_count} movements recorded (Tillytics-only).`);
      }
      setApproveOpen(false);
      setCurrentSession(null);
      load();
      refreshToday?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to approve');
    } finally {
      setApproving(false);
    }
  };

  const runSearch = async () => {
    try {
      const res = await kApi().get('/stock-take/items', { params: { search: searchQuery || undefined, limit: 50 } });
      setSearchResults(res.data.items || []);
      setSearchProvider(res.data.provider);
      setSearchError(res.data.error || null);
    } catch (err) {
      setSearchError(err?.response?.data?.detail || 'Search failed');
    }
  };

  useEffect(() => {
    if (!currentSession || currentSession.kind !== 'free') return;
    const h = setTimeout(runSearch, 300);
    return () => clearTimeout(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, currentSession?.id]);

  const countedIds = useMemo(
    () => new Set((currentSession?.lines || []).map(l => l.variation_external_id)),
    [currentSession],
  );

  const cardBg = t('bg-slate-900 border-slate-800 text-slate-100', 'bg-white border-slate-200 text-slate-900');
  const mutedText = t('text-slate-400', 'text-slate-500');
  const strongText = t('text-white', 'text-slate-900');
  const softBg = t('bg-slate-800/50', 'bg-slate-100');
  const inputCls = t('bg-slate-800 border-slate-700 text-white', 'bg-white border-slate-300 text-slate-900');

  if (loading) {
    return <p className={mutedText}>Loading stock take…</p>;
  }

  // ── No active session: pick sheet / free count ─────────────────────────
  if (!currentSession) {
    if (!canStart) {
      return (
        <Card className={cardBg} data-testid="stock-take-no-permission">
          <CardContent className="p-6 flex items-center gap-3">
            <LockKeyhole className="w-6 h-6 opacity-70" />
            <div>
              <p className={`font-medium ${strongText}`}>Ask a manager to start a stock take</p>
              <p className={`text-sm ${mutedText}`}>
                This kiosk is set to allow managers only to begin stock takes.
              </p>
            </div>
          </CardContent>
        </Card>
      );
    }
    return (
      <div className="space-y-4" data-testid="stock-take-picker">
        <div className={`text-sm ${mutedText}`}>
          Start a count from a saved sheet, or pick <b>Free count</b> to count any item.
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {sheets.map(sheet => (
            <Card key={sheet.id} className={`${cardBg} cursor-pointer hover:opacity-95`} data-testid={`sheet-${sheet.id}`}>
              <CardContent className="p-4 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className={`font-medium truncate ${strongText}`}>{sheet.name}</p>
                  <p className={`text-xs ${mutedText}`}>
                    {sheet.item_count} items · Last done {sheet.last_completed_at ? new Date(sheet.last_completed_at).toLocaleDateString() : 'never'}
                  </p>
                </div>
                <Button size="sm" onClick={() => startSession('template', sheet.id)} data-testid={`start-sheet-${sheet.id}`}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </CardContent>
            </Card>
          ))}
          <Card className={`${cardBg} border-dashed`} data-testid="free-count-card">
            <CardContent className="p-4 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className={`font-medium ${strongText}`}>Free count</p>
                <p className={`text-xs ${mutedText}`}>Pick any items on the fly</p>
              </div>
              <Button size="sm" variant="outline" onClick={() => startSession('free')} data-testid="start-free-count">
                <PlusCircle className="w-4 h-4 mr-1" /> Start
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // ── Active draft session ───────────────────────────────────────────────
  const lines = currentSession.lines || [];
  const variances = lines.filter(l => l.variance !== null && l.variance !== undefined && l.variance !== 0);
  const isTemplate = currentSession.kind === 'template';
  const expected = currentSession.expected_item_ids || [];
  const remainingTemplateItems = isTemplate
    ? expected.filter(id => !countedIds.has(id))
    : [];

  return (
    <div className="space-y-4" data-testid="stock-take-active">
      {/* Session header */}
      <Card className={cardBg}>
        <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <p className={`font-medium ${strongText} flex items-center gap-2`}>
              <ClipboardList className="w-4 h-4" />
              {currentSession.sheet_name || 'Free count'}
              <Badge variant="outline" className={t('bg-slate-800 border-slate-600 text-slate-200', 'bg-slate-100 border-slate-300 text-slate-700')}>Draft</Badge>
            </p>
            <p className={`text-xs ${mutedText}`}>
              Started by {currentSession.created_by_employee_name} · {lines.length} line{lines.length === 1 ? '' : 's'} counted
              {isTemplate ? ` · ${remainingTemplateItems.length} left on sheet` : ''}
            </p>
          </div>
          <div className="flex gap-2">
            {canApprove && lines.length > 0 && (
              <Button onClick={() => setApproveOpen(true)} data-testid="stock-take-approve-btn">
                <CheckCircle2 className="w-4 h-4 mr-1" /> Finish & approve
              </Button>
            )}
            {!canApprove && lines.length > 0 && (
              <Badge variant="outline" className={t('bg-amber-950/40 border-amber-800 text-amber-200', 'bg-amber-50 border-amber-200 text-amber-700')}>
                Manager approval needed
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Free-count search */}
      {!isTemplate && (
        <Card className={cardBg}>
          <CardContent className="p-4 space-y-3">
            <div className="flex gap-2 items-center">
              <Search className={`w-4 h-4 ${mutedText}`} />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search inventory…"
                className={inputCls}
                data-testid="stock-take-search"
              />
            </div>
            {searchError && (
              <div className={`text-xs flex items-center gap-2 ${t('text-amber-300', 'text-amber-700')}`}>
                <AlertTriangle className="w-3 h-3" /> {searchError}
              </div>
            )}
            <div className="space-y-1 max-h-72 overflow-auto">
              {searchResults.length === 0 && !searchError && (
                <p className={`text-sm ${mutedText}`}>{searchProvider ? 'No items match' : 'Type to search'}</p>
              )}
              {searchResults.map(item => (
                <button
                  key={item.variation_external_id}
                  onClick={() => openLine(item)}
                  className={`w-full text-left px-3 py-2 rounded-md flex items-center justify-between ${softBg} hover:opacity-90`}
                  data-testid={`item-row-${item.variation_external_id}`}
                >
                  <span className={strongText}>{item.name}{item.variation_name ? ` · ${item.variation_name}` : ''}</span>
                  <span className={`text-xs ${mutedText}`}>
                    In stock: {item.in_stock ?? '—'}
                    {countedIds.has(item.variation_external_id) && ' · counted'}
                  </span>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Template — remaining items to count */}
      {isTemplate && remainingTemplateItems.length > 0 && (
        <Card className={cardBg}>
          <CardContent className="p-4 space-y-2">
            <p className={`text-sm font-medium ${strongText} flex items-center gap-2`}>
              <PackageSearch className="w-4 h-4" /> Remaining on this sheet
            </p>
            <div className="space-y-1 max-h-72 overflow-auto">
              {remainingTemplateItems.map(id => (
                <button
                  key={id}
                  onClick={() => openLine({ variation_external_id: id, name: id })}
                  className={`w-full text-left px-3 py-2 rounded-md flex items-center justify-between ${softBg} hover:opacity-90`}
                  data-testid={`sheet-item-${id}`}
                >
                  <span className={strongText}>{id}</span>
                  <ChevronRight className={`w-4 h-4 ${mutedText}`} />
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Counted lines */}
      {lines.length > 0 && (
        <Card className={cardBg}>
          <CardContent className="p-4">
            <p className={`text-sm font-medium ${strongText} mb-2`}>Counted lines ({lines.length})</p>
            <div className="space-y-1">
              {lines.map(line => (
                <div key={line.id} className={`flex items-center justify-between px-3 py-2 rounded-md ${softBg}`} data-testid={`line-row-${line.id}`}>
                  <div className="min-w-0">
                    <p className={`text-sm truncate ${strongText}`}>{line.item_name}</p>
                    <p className={`text-xs ${mutedText}`}>
                      {line.previous_count ?? '—'} → <b>{line.new_count}</b>
                      {line.variance !== null && line.variance !== undefined && ` (var ${line.variance > 0 ? '+' : ''}${line.variance})`}
                      {line.reason ? ` · ${line.reason.replace(/_/g, ' ')}` : ''}
                    </p>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => deleteLine(line.id)} data-testid={`del-line-${line.id}`}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Line entry dialog with numeric keypad */}
      <Dialog open={!!lineDialog} onOpenChange={() => setLineDialog(null)}>
        <DialogContent className={t('bg-slate-900 border-slate-800 text-white', 'bg-white border-slate-200 text-slate-900')} data-testid="stock-take-line-dialog">
          <DialogHeader>
            <DialogTitle>{lineDialog?.item_name}</DialogTitle>
            <DialogDescription className={mutedText}>
              Previous count: {lineDialog?.previous_count ?? '—'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className={mutedText}>On-hand count</Label>
              <Input
                type="number" step="1" inputMode="decimal" autoFocus
                value={lineQty}
                onChange={(e) => setLineQty(e.target.value)}
                className={`text-3xl h-16 text-center ${inputCls}`}
                data-testid="line-qty-input"
              />
            </div>
            <div className="grid grid-cols-3 gap-2">
              {['1','2','3','4','5','6','7','8','9','.','0','⌫'].map((k, i) => (
                <Button
                  key={i}
                  variant="outline"
                  className={`h-12 text-lg ${t('bg-slate-800 border-slate-700 hover:bg-slate-700 text-white', 'bg-slate-100 border-slate-300 hover:bg-slate-200 text-slate-900')}`}
                  onClick={() => {
                    if (k === '⌫') setLineQty(v => v.slice(0, -1));
                    else setLineQty(v => v + k);
                  }}
                  data-testid={`line-pad-${k}`}
                >
                  {k}
                </Button>
              ))}
            </div>
            <div>
              <Label className={mutedText}>Reason (optional)</Label>
              <Select value={lineReason} onValueChange={setLineReason}>
                <SelectTrigger className={inputCls} data-testid="line-reason-select"><SelectValue placeholder="Choose a reason" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None</SelectItem>
                  {REASONS.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {lineReason === 'other' && (
              <div>
                <Label className={mutedText}>Note</Label>
                <Input value={lineNote} onChange={(e) => setLineNote(e.target.value)} className={inputCls} data-testid="line-note-input" />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLineDialog(null)}>Cancel</Button>
            <Button onClick={saveLine} data-testid="line-save-btn">Save line</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approve full-screen sheet */}
      <Dialog open={approveOpen} onOpenChange={setApproveOpen}>
        <DialogContent className={`max-w-2xl ${t('bg-slate-900 border-slate-800 text-white', 'bg-white border-slate-200 text-slate-900')}`} data-testid="stock-take-approve-dialog">
          <DialogHeader>
            <DialogTitle>Finish &amp; approve stock take</DialogTitle>
            <DialogDescription className={mutedText}>
              {lines.length} item{lines.length === 1 ? '' : 's'} counted · {variances.length} variance{variances.length === 1 ? '' : 's'}.
              Once approved, stock movements are recorded and
              {pushMode === 'never' ? ' kept Tillytics-only.' :
                pushMode === 'immediate' ? ' pushed to the POS per line.' :
                ' pushed to the POS in one batch.'}
              Approved counts are locked — a manager can add a correction later.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-72 overflow-auto space-y-1">
            {lines.map(l => (
              <div key={l.id} className={`flex justify-between text-sm px-3 py-2 rounded ${softBg}`}>
                <span className={strongText}>{l.item_name}</span>
                <span className={mutedText}>
                  {l.previous_count ?? '—'} → <b className={strongText}>{l.new_count}</b>
                  {l.variance !== null && l.variance !== undefined && ` (${l.variance > 0 ? '+' : ''}${l.variance})`}
                  {l.reason ? ` · ${l.reason.replace(/_/g, ' ')}` : ''}
                </span>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveOpen(false)}>Back</Button>
            <Button onClick={approve} disabled={approving} data-testid="stock-take-approve-confirm">
              {approving ? 'Approving…' : 'Approve stock take'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
